import { memo, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';

import { syncMessenger } from '../../actions/messenger';
import { isMessengerAllowed } from '../../selectors/messenger';

// Интервал фонового опроса списка чатов (новые диалоги + счётчик непрочитанных).
// 12 c — компромисс между «живостью» переписки и нагрузкой на IS для планшета.
const POLL_INTERVAL_MS = 12000;

const selectOnline = (state) => {
    const online = get(state, 'online');

    return typeof online === 'boolean' ? online : get(online, 'status', true);
};

/**
 * Изолированный менеджер фоновой синхронизации мессенджера.
 *  - Доотправляет очередь и обновляет список чатов при восстановлении сети
 *    (offline → online) — «автосинхронизация при появлении сети» из ТЗ.
 *  - Легкий поллинг при наличии сети для обновления счётчика непрочитанных.
 *
 * Полностью бездействует, если мессенджер выключен фиче-флагом или нет авторизации.
 * Не использует общий механизм обмена EFB (Get/Send data), поэтому не влияет на него.
 */
export const MessengerSyncManager = memo(() => {
    const dispatch = useDispatch();
    const allowed = useSelector(isMessengerAllowed);
    const isOnline = useSelector(selectOnline);
    const userId = useSelector((state) => get(state, 'login.user.id'));
    const prevOnline = useRef(isOnline);

    // Автосинхронизация при восстановлении сети.
    useEffect(() => {
        const wasOffline = prevOnline.current === false;
        prevOnline.current = isOnline;

        if (allowed && userId && isOnline && wasOffline) {
            dispatch(syncMessenger());
        }
    }, [allowed, userId, isOnline, dispatch]);

    // Лёгкий поллинг для списка чатов и счётчика непрочитанных + мгновенное
    // обновление при возврате фокуса/видимости вкладки (не ждём тик таймера).
    useEffect(() => {
        if (!allowed || !userId) {
            return undefined;
        }

        // syncMessenger сам досылает очередь принудительно (сетевой «зонд») и при
        // успехе восстанавливает online-флаг. Поэтому опрашиваем безусловно —
        // иначе при «залипшем» офлайн-флаге очередь никогда бы не досылалась.
        const sync = () => {
            dispatch(syncMessenger());
        };

        const timer = setInterval(sync, POLL_INTERVAL_MS);

        const onVisible = () => {
            if (document.visibilityState === 'visible') {
                sync();
            }
        };

        window.addEventListener('focus', sync);
        document.addEventListener('visibilitychange', onVisible);

        return () => {
            clearInterval(timer);
            window.removeEventListener('focus', sync);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [allowed, userId, dispatch]);

    return null;
});

MessengerSyncManager.displayName = 'MessengerSyncManager';
